// frontend/src/main.js
import axios from "axios";
import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import { getAccessToken } from "@/auth/sessionTokens";
import { i18n } from "@/i18n";
import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";

import "vuetify/styles";
import "@mdi/font/css/materialdesignicons.css"; // Ensure MDI Icons are imported
import "./assets/styles.scss"; // Import the global SCSS file
import Vue3StarRatings from "vue3-star-ratings";
import { VTimePicker } from "vuetify/labs/VTimePicker";
import { VDateInput } from "vuetify/labs/VDateInput";
import { autoAnimatePlugin } from "@formkit/auto-animate/vue";
import {
    VListItem,
    VListItemTitle,
    VListItemSubtitle,
    VCombobox,
} from "vuetify/components";

export const vuetify = createVuetify({
    theme: {
        defaultTheme: "myCustomTheme",
        themes: {
            myCustomTheme: {
                dark: false,
                colors: {
                    primary: "#123666",
                    bgSecondary: "#e2f0f8",
                },
            },
        },
    },
    components: {
        ...components,
        VTimePicker,
        VDateInput,
        VListItem,
        VListItemTitle,
        VListItemSubtitle,
        VCombobox,
    },
    directives,
    defaults: {
        VCombobox: {
            variant: "outlined",
            rounded: "lg",
        },
        VCard: {
            rounded: "lg",
            elevation: 3,
        },
        VTextField: {
            variant: "outlined",
            validateOn: "blur",
            rounded: "lg",
        },
        VSelect: {
            variant: "outlined",
            rounded: "lg",
        },
        VFileInput: {
            variant: "outlined",
            rounded: "lg",
        },
        VBtn: {
            variant: "flat",
            rounded: "lg",
            size: "x-large",
            color: "primary",
            elevation: 5,
            textTransform: "unset",
            fontWeight: "bold",
        },
        VAutocomplete: {
            variant: "outlined",
            rounded: "lg",
        },
        VTextarea: {
            variant: "outlined",
            rounded: "lg",

            rows: "2",
        },
    },
});

// Import translation files
import en from "./locales/en.json";
import ro from "./locales/ro.json";
import de from "./locales/de.json";
import hu from "./locales/hu.json";
import fr from "./locales/fr.json";

// Define your translations
const messages = {
    en: en,
    ro: ro,
    de: de,
    hu: hu,
    fr: fr,
};

const app = createApp(App);

app.component("Vue3StarRatings", Vue3StarRatings);

// Base URL for public assets (process.env.BASE_URL replaced at build time by Vue CLI)
app.config.globalProperties.$baseUrl = process.env.BASE_URL || "/";

// Global mixin to make translations reactive
app.mixin({
    computed: {
        // Dummy computed property that depends on the locale
        i18nLocale() {
            return this.$i18n.locale;
        },
    },
});
// **Dispatch the initial screen size detection**
store.dispatch("updateScreenSize");

// **Attach resize event listener**
window.addEventListener("resize", () => {
    store.dispatch("updateScreenSize");
});

// **Cleanup event listener on unmount**
app.config.globalProperties.$destroy = () => {
    window.removeEventListener("resize", () => {
        store.dispatch("updateScreenSize");
    });
};

// Set Axios default header from this tab's session storage if it exists
const token = getAccessToken();
if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
}

// Create and mount the app
app.use(store)
    .use(router)
    .use(i18n)
    .use(vuetify)
    .use(autoAnimatePlugin)
    .mount("#app");
